import { useEffect } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { notify } from '@/components/Notice/Notice';
import { XERO_STATE, type XeroConnection, xeroApi } from '@/features/xero/api';
import { COPY } from '@/features/xero/copy';
import { xeroConnectionQuery, xeroKeys } from '@/features/xero/queries';
import { ApiError } from '@/lib/api';

/**
 * The connection as a page draws it: the state the API last read off Xero, or reconnecting while
 * somebody's Try again is asking. Null until the first answer, so nothing claims a state early.
 */
export function useXeroConnection(): XeroConnection | null {
    const { data } = useQuery(xeroConnectionQuery);
    const checking = useIsMutating({ mutationKey: xeroKeys.check }) > 0;
    if (!data) return null;
    return checking ? { ...data, state: XERO_STATE.syncing } : data;
}

export function useXeroActions() {
    const client = useQueryClient();
    const settle = (next: XeroConnection) => client.setQueryData(xeroKeys.connection, next);
    const failed = (error: unknown) =>
        notify.error(COPY.failed, {
            description: error instanceof ApiError ? error.problem.detail : undefined,
        });

    const check = useMutation({
        mutationKey: xeroKeys.check,
        mutationFn: xeroApi.check,
        onSuccess: settle,
        onError: failed,
    });
    // Leaves the page for Xero's approval page; Xero brings the admin back to this one.
    const connect = useMutation({
        mutationFn: () => xeroApi.connect(`${window.location.pathname}${window.location.search}`),
        onSuccess: ({ url }) => window.location.assign(url),
        onError: failed,
    });
    const disconnect = useMutation({
        mutationFn: xeroApi.disconnect,
        onSuccess: (next) => {
            settle(next);
            notify.success(COPY.disconnect.done);
        },
        onError: failed,
    });
    return { check, connect, disconnect };
}

type Outcome = keyof typeof COPY.outcome;

/**
 * Announces how a Connect came back from Xero, which the API writes into the fragment of the page
 * it returns to (#xero=connected), then takes the fragment off the address so a reload or a shared
 * link does not announce it again.
 */
export function useXeroReturn() {
    const client = useQueryClient();
    useEffect(() => {
        const found = /^#xero=([a-z]+)$/.exec(window.location.hash)?.[1];
        if (!found) return;
        window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${window.location.search}`,
        );
        if (!Object.hasOwn(COPY.outcome, found)) return;
        const outcome = COPY.outcome[found as Outcome];
        notify[outcome.tone](outcome.title, {
            description: 'description' in outcome ? outcome.description : undefined,
        });
        void client.invalidateQueries({ queryKey: xeroKeys.connection });
    }, [client]);
}
